/**
 * What we still owe this buyer.
 *
 * A promise made at the desk and not written down is worth nothing, and a
 * promise written down and not tracked is worse than nothing — it is a
 * written obligation with no reminder attached. So this panel does both: it
 * prints the page the buyer signs, and it keeps the item on a list until
 * somebody says the work is done.
 *
 * The AI drafts, it does not decide. Whatever comes back lands in an editable
 * box and has to be saved by a person before it can print.
 */

import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Button, Panel, ErrorNote, Confirm } from "../../components/ui";
import { TextField, DueDateField, PastDateField } from "../../components/fields";
import { usDate } from "../../forms/mappings";
import { openForPrint } from "./buildPacket";
import { archiveDealDocument, ArchiveError } from "../../lib/documents";
import { draftWeOwe, workerConfigured } from "../../lib/stripe";
import { SaveBar, SaveError, SaveStatus } from "../../components/save";
import { useSaveState } from "../../lib/saving";
import {
  listWeOwes, addWeOwe, updateWeOwe, completeWeOwe, reopenWeOwe, deleteWeOwe,
  buildWeOweForDeal, type WeOwe,
} from "./weOwes";

const today = () => new Date().toISOString().slice(0, 10);

interface Draft {
  id: string | null;        // null while it is new
  title: string;
  body: string;
  dueDate: string;
}

const EMPTY: Draft = { id: null, title: "", body: "", dueDate: "" };

export default function WeOwePanel({ dealId, dealNo, dealStatus, vehicle, vin, buyer }: {
  dealId: string;
  dealNo: string;
  dealStatus: string;
  vehicle: string;
  vin: string;
  buyer: string;
}) {
  const [rows, setRows] = useState<WeOwe[]>([]);
  const [dealer, setDealer] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);

  const [prompt, setPrompt] = useState("");
  const [drafting, setDrafting] = useState(false);

  const [printing, setPrinting] = useState(false);
  const [closing, setClosing] = useState<WeOwe | null>(null);
  const [closeDate, setCloseDate] = useState(today());
  const [closeNote, setCloseNote] = useState("");
  const [removing, setRemoving] = useState<WeOwe | null>(null);

  /* The original of whatever is being edited, so "unsaved" means actually
     changed rather than merely opened. */
  const pristine = useRef<Draft>(EMPTY);
  const dirty = draft !== null && (
    draft.title !== pristine.current.title
    || draft.body !== pristine.current.body
    || draft.dueDate !== pristine.current.dueDate
  );
  /* Unsaved changes / Saving… / Saved ✓ 3:04 PM / Not saved, and the
     leave-page warning while there is unsaved text. */
  const weSave = useSaveState({ dirty, what: "this we-owe" });
  const saving = weSave.saving;
  /* Mark done, reopen, remove: instant, but each still says it landed. */
  const weAct = useSaveState();
  const [actLabel, setActLabel] = useState("Saved");

  const load = () => {
    setLoading(true);
    listWeOwes(dealId)
      .then((r) => { setRows(r); setErr(null); })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [dealId]);

  /* Only for the drafter's context, so the clauses name the right business.
     What actually prints comes from dealer_settings via the form context. */
  useEffect(() => {
    supabase.from("dealer_settings").select("legal_name,dba").maybeSingle()
      .then(({ data }) => setDealer(data ? (data.dba ?? data.legal_name) : ""));
  }, []);

  const open = rows.filter((r) => r.status === "open");
  const done = rows.filter((r) => r.status !== "open");

  const startNew = () => {
    pristine.current = EMPTY;
    setDraft({ ...EMPTY });
    setPrompt("");
    weSave.reset();
    setErr(null);
  };

  const startEdit = (w: WeOwe) => {
    const d: Draft = { id: w.id, title: w.title, body: w.body, dueDate: w.due_date ?? "" };
    pristine.current = d;
    setDraft({ ...d });
    setPrompt("");
    weSave.reset();
    setErr(null);
  };

  const cancel = () => {
    if (dirty && !window.confirm("Discard what you have written?")) return;
    setDraft(null);
    setPrompt("");
    weSave.reset();
  };

  const askAi = async () => {
    if (!prompt.trim() || !draft) return;
    setDrafting(true);
    setErr(null);
    try {
      const out = await draftWeOwe(prompt, {
        vehicle, vin, buyer, dealer, today: usDate(today()),
      });
      setDraft({
        ...draft,
        title: draft.title.trim() || out.title,
        body: out.body,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.title.trim()) { weSave.fail("Give it a short title."); return; }
    if (!draft.body.trim()) { weSave.fail("There is nothing written to save."); return; }

    setErr(null);
    const sent = draft;
    const ok = await weSave.run(async () => {
      const fields = { title: sent.title, body: sent.body, dueDate: sent.dueDate || null };
      if (sent.id) {
        await updateWeOwe(sent.id, fields);
      } else {
        await addWeOwe(dealId, fields);
      }
    }, { toast: "We-owe saved" });
    if (!ok) return;            // the text stays in the box
    pristine.current = { ...sent };
    setDraft(null);
    load();
  };

  const print = async () => {
    setPrinting(true);
    setErr(null);
    setNote(null);
    try {
      const bytes = await buildWeOweForDeal(dealId);
      openForPrint(bytes, `${dealNo}-we-owe.pdf`);
      // Filed for the same reason the packet is: the signed page is the
      // record, and it has to still exist when somebody disputes it.
      if (dealStatus === "sold") {
        try {
          await archiveDealDocument(bytes, { dealId, docType: "we_owe", dealStatus });
          setNote("Printed and filed.");
        } catch (e) {
          setNote(e instanceof ArchiveError
            ? `Printed, but not filed: ${e.message}`
            : "Printed, but not filed.");
        }
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setPrinting(false);
    }
  };

  const confirmClose = async () => {
    if (!closing) return;
    setActLabel(`"${closing.title}" marked done`);
    const ok = await weAct.run(() => completeWeOwe(closing.id, closeDate, closeNote),
      { toast: "Marked done" });
    // On a failure the dialog stays open with the date and note still in it.
    if (!ok) return;
    setClosing(null);
    setCloseNote("");
    setCloseDate(today());
    load();
  };

  const reopen = async (w: WeOwe) => {
    setActLabel(`"${w.title}" reopened`);
    if (await weAct.run(() => reopenWeOwe(w.id), { toast: "Reopened" })) load();
  };

  const figure = open.length
    ? `${open.length} outstanding`
    : rows.length ? "all done" : undefined;

  return (
    <Panel title="What we owe" rail="work" figure={figure}>
      {err && <ErrorNote>{err}</ErrorNote>}
      {note && <p className="hint">{note}</p>}
      <p className="savenote"><SaveStatus save={weAct} hideWhenIdle savedLabel={actLabel} /></p>
      {!closing && <SaveError save={weAct} hint="Nothing on this list was changed." />}

      {loading ? (
        <p className="hint">Loading…</p>
      ) : rows.length === 0 && !draft ? (
        <p className="hint">
          Nothing owed on this deal. If you promised the buyer anything — an
          alignment, a second key, a bumper touched up — write it here and
          print it. A promise that is not written down is not enforceable, and
          the Buyers Guide the buyer signs says exactly that.
        </p>
      ) : null}

      {/* ---- the list ---- */}
      {open.length > 0 && (
        <div className="item">
          {open.map((w) => (
            <div className="li stack" key={w.id}>
              <div className="li-top">
                <span>
                  <strong>{w.title}</strong>
                  {w.due_date && (
                    <span className={w.due_date < today() ? "flag late" : "flag"}>
                      {w.due_date < today() ? "overdue " : "by "}{usDate(w.due_date)}
                    </span>
                  )}
                </span>
                <span className="rowacts">
                  <button className="linkbtn tiny" onClick={() => startEdit(w)}>edit</button>
                  <button className="linkbtn tiny" onClick={() => setClosing(w)}>mark done</button>
                  <button className="linkbtn tiny danger" onClick={() => setRemoving(w)}>
                    remove
                  </button>
                </span>
              </div>
              <p className="owebody">{w.body}</p>
            </div>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <details className="donelist">
          <summary>{done.length} completed</summary>
          {done.map((w) => (
            <div className="li" key={w.id}>
              <span>
                <s>{w.title}</s>
                {w.completed_on && <span className="flag ok">done {usDate(w.completed_on)}</span>}
                {w.completion_note && <span className="hint inline">{w.completion_note}</span>}
              </span>
              <button className="linkbtn tiny" disabled={weAct.saving}
                      onClick={() => void reopen(w)}>
                reopen
              </button>
            </div>
          ))}
        </details>
      )}

      {/* ---- writing one ---- */}
      {draft ? (
        <div className="drafter">
          <div className="row2">
            <TextField label="Title" value={draft.title}
                       hint="Two or three words — Alignment, Second key"
                       onChange={(v) => setDraft({ ...draft, title: v })} />
            <DueDateField label="Promised by" value={draft.dueDate}
                          hint="The appointment or deadline, if there is one"
                          onChange={(v) => setDraft({ ...draft, dueDate: v })} />
          </div>

          {workerConfigured() && (
            <div className="aibox">
              <label htmlFor="weowe-prompt">Describe what you promised</label>
              <textarea
                id="weowe-prompt"
                rows={2}
                value={prompt}
                placeholder="fix the alignment, appointment at the shop on the 12th, if it needs parts we cover them"
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div className="actions">
                <Button variant="ghost" disabled={drafting || !prompt.trim()} onClick={askAi}>
                  {drafting ? "Writing…" : draft.body ? "Rewrite with AI" : "Write it for me"}
                </Button>
                <span className="hint">
                  Plain words are fine. Read what comes back before you save it.
                </span>
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor="weowe-body">What we owe, as it will print</label>
            <textarea
              id="weowe-body"
              rows={9}
              value={draft.body}
              placeholder={"1. Alignment. …\n\n2. Appointment. …"}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
            <p className="hint">
              One numbered clause per paragraph. The page renumbers them, so the
              numbers here are only for reading.
            </p>
          </div>

          <SaveBar save={weSave} onSave={() => void save()}
                   label={draft.id ? "Save changes" : "Save it"}
                   idle="Nothing written yet"
                   retryHint="Your text is still here — press Save to try again.">
            <Button variant="ghost" disabled={saving} onClick={cancel}>Cancel</Button>
          </SaveBar>
        </div>
      ) : (
        <div className="actions">
          <Button variant="ghost" onClick={startNew}>Add something we owe</Button>
          {open.length > 0 && (
            <Button disabled={printing} onClick={print}>
              {printing ? "Opening…" : "Print we owe"}
            </Button>
          )}
          <SaveStatus save={weSave} hideWhenIdle savedLabel="We-owe saved" />
        </div>
      )}

      {open.length > 0 && !draft && (
        <p className="taxnote">
          The printed page lists everything still open, with signature lines for
          the buyer and for you. Print two — the buyer keeps one. Completed
          items are left off, so a reprint never re-promises finished work.
        </p>
      )}

      {closing && (
        <Confirm
          message={`Mark "${closing.title}" done?`}
          confirmLabel={weAct.saving ? "Saving…" : "Mark done"}
          danger={false}
          cancelLabel="Not yet"
          onCancel={() => { setClosing(null); weAct.dismiss(); }}
          onConfirm={confirmClose}
        >
          <p className="hint">
            It comes off the outstanding list and stops printing on the we-owe.
          </p>
          <PastDateField label="Completed on" value={closeDate} required
                         onChange={setCloseDate} />
          <TextField label="Note" value={closeNote}
                     hint="Optional — what was actually done, or which shop did it"
                     onChange={setCloseNote} />
          <SaveError save={weAct} hint="It is still open. The date and note are still here — try again." />
        </Confirm>
      )}

      {removing && (
        <Confirm
          message={`Remove "${removing.title}"?`}
          confirmLabel="Remove"
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            setActLabel(`"${removing.title}" removed`);
            const ok = await weAct.run(() => deleteWeOwe(removing.id), { toast: "We-owe removed" });
            setRemoving(null);
            if (ok) load();
          }}
        >
          <p className="hint">
            This deletes the promise. If the work was done, mark it done instead
            — that keeps the record of what was owed and when it was settled.
          </p>
        </Confirm>
      )}
    </Panel>
  );
}
